import {
  AspectImage,
  Box,
  Button,
  Col,
  FlexGrid,
  Row,
  Text,
} from "@vinomofo/components";
import { CircleMinusIcon, ToggleIcon, TrashCanIcon } from "@vinomofo/icons";
import {
  ErrorNotifier,
  errorDataBuilder,
} from "../../../api/src/utils/error-notifier";

import { CartItem } from "../../contexts/cart/types";
import { CurrencyLabel } from "../currency";
import { StaticImage } from "gatsby-plugin-image";
import config from "../../../config.json";
import styled from "styled-components";
import { trackOfferDetailsEvent } from "../../utils/event-tracking";
import useAuth from "../../hooks/use-auth";
import { useCart } from "../../hooks/use-cart";
import { useLocation } from "@reach/router";
import useOneClickCheckout from "../../hooks/use-one-click-checkout";
import { EventSubType, EventType } from "../../types";
import { hasSpiritsInPath } from "../../utils/is-page";

const lowInventoryPerItemWarning = config.LOW_INVENTORY_PER_ITEM_WARNING_CART;
const lowInventoryCount =
  config[process.env.GATSBY_VM_MARKET || "AU"].LOW_INVENTORY_COUNT;

export interface CartItemRowProps {
  cartItem: CartItem;
}

const Image = styled(AspectImage)`
  opacity: ${({ isLoading }) => (isLoading ? 0.5 : 1)};
`;

const CartItemContainer = styled(Box)`
  &:last-child {
    border-bottom: none;
  }
`;

const LinkText = styled(Text)`
  color: #131314;
  text-decoration: none;
  &:hover {
    color: #77b204;
    text-decoration: underline;
  }
`;

const StyledTextCase = styled(Text)`
  -webkit-font-smoothing: unset;
  -moz-osx-font-smoothing: unset;
  min-width: 75px;
`;

const StyledRemoveButton = styled(Button)`
  svg path {
    fill: rgb(98, 100, 104) !important;
  }

  :hover svg path {
    fill: #77b204 !important;
  }
`;

export const CartItemRow = ({ cartItem }: CartItemRowProps): JSX.Element => {
  const {
    id,
    name,
    imageUrl,
    quantity,
    extendedSalePrice,
    isSoldOut,
    productId,
    slug,
    currentStock,
    sku,
    offerId,
    packSize,
    skuType,
    isSpirit,
  } = cartItem;
  const {
    isProcessing,
    isBuying,
    isRedirecting: oneClickCheckoutRedirecting,
  } = useOneClickCheckout();

  const { removeItem, isLoading, cart, updateItem } = useCart();

  const darkTextColor = isLoading || isSoldOut ? "disabled" : "text.heading";
  const textColor = isLoading || isSoldOut ? "disabled" : "heading";
  const location = useLocation();
  const { user } = useAuth();

  const vmWmSiteName = process.env.GATSBY_VM_WM_SITE_NAME;
  const vmWmAppUrl = vmWmSiteName ? process.env.GATSBY_VM_WM_APP_URL : "";

  let href = slug;
  if (vmWmSiteName) {
    if (isSpirit && vmWmSiteName === "VM") {
      href = vmWmAppUrl + slug;
    } else if (!isSpirit && vmWmSiteName === "WM") {
      href = vmWmAppUrl + slug;
    }
  }

  const cartOfferDetails = {
    ...cartItem,
    offerId,
    cartId: cart.id,
    name,
    productId,
    quantity,
    SKU: sku,
    changed_by: 1,
    imageUrl,
    url: `${location.origin}${slug}`,
    customer_group: user?.customer_group?.name || "No Group",
  };

  const handleRemoveItem = async (id: string) => {
    try {
      await removeItem(id);
      trackOfferDetailsEvent("Product Removed", {
        eventType: EventType.Click,
        ...cartOfferDetails,
      });
    } catch (error) {
      const statusCode = error.response ? error.response.status : 400;
      ErrorNotifier.notify(error, errorDataBuilder("FE", statusCode, error));
    }
  };

  const handleIncreaseQuantity = async () => {
    try {
      const newQuantity = quantity + 1;
      await updateItem({ cartItemId: id, productId, quantity: newQuantity });
      trackOfferDetailsEvent("Product Quantity Increased", {
        eventType: EventType.Conversion,
        eventSubType: EventSubType.AddToCart,
        new_quantity: newQuantity,
        ...cartOfferDetails,
      });
    } catch (error) {
      const statusCode = error.response ? error.response.status : 400;
      ErrorNotifier.notify(error, errorDataBuilder("FE", statusCode, error));
    }
  };

  const handleDecreaseQuantity = async () => {
    try {
      const newQuantity = quantity - 1;
      if (quantity === 1) {
        await removeItem(id);
        trackOfferDetailsEvent("Product Removed", {
          eventType: EventType.Click,
          ...cartOfferDetails,
        });
      } else {
        await updateItem({ cartItemId: id, productId, quantity: newQuantity });
        trackOfferDetailsEvent("Product Quantity Decreased", {
          eventType: EventType.Click,
          new_quantity: newQuantity,
          ...cartOfferDetails,
        });
      }
    } catch (error) {
      const statusCode = error.response ? error.response.status : 400;
      ErrorNotifier.notify(error, errorDataBuilder("FE", statusCode, error));
    }
  };

  const isDisabled = isBuying || isProcessing || oneClickCheckoutRedirecting;

  return (
    <CartItemContainer
      borderBottom="1px solid"
      borderBottomColor="divider"
      py={[2]}
    >
      <Box display="flex" justifyContent="space-between" px={2} py={2}>
        <Box
          width={45}
          flexShrink={0}
          flexGrow={0}
          mr={10}
          alignItems="center"
          display="inline-grid"
        >
          {imageUrl ? (
            <Image
              ratio={1}
              srcSet={imageUrl}
              isLoading={isLoading || isSoldOut}
            />
          ) : (
            <StaticImage
              src="../../images/place-holders/bottle-red-bordeaux.png"
              style={{
                width: "100%",
                height: "46px",
                overflow: "hidden",
                verticalAlign: "middle",
              }}
              imgStyle={{
                objectFit: "contain",
                verticalAlign: "middle",
              }}
              alt="placeholder-bottle"
            />
          )}
        </Box>
        <Box display="flex" flexGrow={1} alignItems="center">
          <Box flex={3} pr="10px">
            <LinkText color={textColor} fontSize="14px">
              <LinkText as="a" href={href}>
                {name}
              </LinkText>
            </LinkText>
            {isSoldOut && (
              <Text fontSize={0} fontWeight={500} color="deal">
                Out-of-stock
              </Text>
            )}
          </Box>
          <Box>
            <Box
              flexGrow="0"
              display="flex"
              justifyContent="center"
              px="10px"
              width={180}
            >
              <FlexGrid>
                <Row flexWrap="nowrap">
                  <Col display="flex" justifyContent="center">
                    <Button
                      disabled={isLoading || isSoldOut || isDisabled}
                      onClick={handleDecreaseQuantity}
                      data-testid="decrease-quantity"
                      color="brand"
                      style={{
                        opacity: isLoading || isSoldOut || isDisabled ? 0.1 : 1,
                        marginRight: "8px",
                      }}
                    >
                      <CircleMinusIcon size="16px" />
                    </Button>
                  </Col>
                  <Col display="flex" justifyContent="center">
                    <Text
                      fontSize={3}
                      fontFamily="heading"
                      color={darkTextColor}
                      data-testid="product-quantity"
                    >
                      {quantity}
                    </Text>
                  </Col>
                  <Col display="flex" justifyContent="center">
                    <Button
                      disabled={isLoading || isSoldOut || isDisabled}
                      onClick={handleIncreaseQuantity}
                      data-testid="increase-quantity"
                      color="brand"
                      style={{
                        opacity: isLoading || isSoldOut || isDisabled ? 0.1 : 1,
                        marginLeft: "8px",
                      }}
                    >
                      <ToggleIcon size="16px" />
                    </Button>
                  </Col>
                </Row>
              </FlexGrid>
              {/* <Text color={textColor} fontSize="14px">
                {quantity}
              </Text> */}
              <Box flexGrow={0} flexBasis="65px">
                <Text
                  fontWeight={500}
                  color={textColor}
                  fontSize="14px"
                  textAlign="right"
                  lineHeight={1.8}
                >
                  <CurrencyLabel
                    amount={extendedSalePrice}
                    showFree
                    isSoldOut
                  />
                </Text>
              </Box>
              <Box
                width="15px"
                flexShrink={0}
                flexGrow={0}
                alignItems="center"
                display="inline-grid"
                ml={2}
                mt="3px"
              >
                <StyledRemoveButton
                  onClick={() => handleRemoveItem(id)}
                  disabled={isLoading}
                  data-testid="remove-item"
                  color={isLoading ? "fieldBorder" : "dorianGray"}
                  hoverColor="mofoGreen"
                  style={{
                    opacity: isLoading ? 0.1 : 1,
                  }}
                  title="Remove item"
                >
                  <TrashCanIcon size="15px" />
                </StyledRemoveButton>
              </Box>
            </Box>

            <Box
              display="flex"
              alignItems="baseline"
              justifyContent={
                currentStock < lowInventoryCount
                  ? skuType !== "NonWineSKU"
                    ? "space-evenly"
                    : "left"
                  : "right"
              }
              pr={
                currentStock < lowInventoryCount
                  ? skuType !== "NonWineSKU"
                    ? "12px"
                    : "0"
                  : "12px"
              }
            >
              {currentStock < lowInventoryCount && (
                <Text
                  fontFamily="heading"
                  textAlign={"left"}
                  fontSize={1}
                  fontWeight={500}
                  color="deal"
                  style={{
                    opacity: isLoading ? 0.1 : 1,
                    paddingLeft: "2px",
                  }}
                >
                  {lowInventoryPerItemWarning}
                </Text>
              )}
              {skuType !== "NonWineSKU" && (
                <StyledTextCase fontSize="12px" textAlign="right">
                  {quantity} x case ({packSize})
                </StyledTextCase>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </CartItemContainer>
  );
};
